import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  CircularProgress,
  Button,
  Pagination,
  PaginationItem,
} from '@mui/material';
import { campaignService, campaignCategoryService } from '../services/api';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { AccessTime, LocationOn } from '@mui/icons-material';
import { getImageUrl, ASSETS } from '../utils/assets';
import { BRAND } from '../theme/pageStyles';
import CampaignShareButtons from '../components/CampaignShareButtons';
import SiteStatsItems from '../components/SiteStatsItems';
import { getCampaignPath } from '../utils/campaignUrl';
import ImageHeroBanner from '../components/ImageHeroBanner';

const BRAND_GREEN = '#477C2E';

const META_GREY = '#6b7280';
// Column themes: Red/Pink, Green, Orange/Yellow (accent + light button bg)
const CARD_THEMES = [
  { accent: '#dc2626', btnBg: '#FFE5E0' },   // red/pink - light peach
  { accent: BRAND_GREEN, btnBg: '#dcfce7' }, // green - light mint
  { accent: '#eab308', btnBg: '#fef9c3' },   // orange/yellow - light pale yellow
];

function formatCampaignDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

const PER_PAGE = 6;

const CampaignsPage = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    campaignCategoryService
      .getAll()
      .then((res) => {
        const list = res.data?.data ?? res.data ?? [];
        setCategories(Array.isArray(list) ? list : []);
      })
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        const params = { status: 'active', limit: 100, page: 1 };
        if (selectedCategoryId) params.category_id = selectedCategoryId;
        const response = await campaignService.getAllCampaigns(params);
        const data = response.data?.data || response.data || [];
        setCampaigns(Array.isArray(data) ? data : []);
        setPage(1);
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, [selectedCategoryId]);

  const totalPages = Math.max(1, Math.ceil(campaigns.length / PER_PAGE));
  const paginatedCampaigns = campaigns.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: BRAND.green }} />
      </Box>
    );
  }

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <ImageHeroBanner
        subtitle="Make a difference"
        title="Our Campaigns"
        description="Explore active campaigns and support causes that create lasting impact in our communities."
        backgroundImage={ASSETS.hero}
        buttonText="Start a campaign"
        buttonLink="/campaigns/submit"
      />

      {/* Campaigns Grid - 3x2, card: image, title, date, location, description, Read More */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg" sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button
              component={RouterLink}
              to="/campaigns/submit"
              variant="contained"
              sx={{
                borderRadius: 28,
                textTransform: 'none',
                fontFamily: '"Poppins", sans-serif',
                bgcolor: BRAND_GREEN,
                '&:hover': { bgcolor: '#3d6a28' },
              }}
            >
              Start a campaign
            </Button>
          </Box>
          {categories.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, justifyContent: 'center' }}>
              <Button
                variant={selectedCategoryId === '' ? 'contained' : 'outlined'}
                onClick={() => setSelectedCategoryId('')}
                sx={{
                  borderRadius: 20,
                  textTransform: 'none',
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 600,
                  ...(selectedCategoryId === '' ? { bgcolor: BRAND_GREEN, '&:hover': { bgcolor: BRAND.greenHover || '#3d6a28' } } : { borderColor: BRAND_GREEN, color: BRAND_GREEN }),
                }}
              >
                All
              </Button>
              {categories.map((cat) => (
                <Button
                  key={cat.id}
                  variant={String(selectedCategoryId) === String(cat.id) ? 'contained' : 'outlined'}
                  onClick={() => setSelectedCategoryId(String(cat.id))}
                  sx={{
                    borderRadius: 20,
                    textTransform: 'none',
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: 600,
                    ...(String(selectedCategoryId) === String(cat.id)
                      ? { bgcolor: BRAND_GREEN, '&:hover': { bgcolor: BRAND.greenHover || '#3d6a28' } }
                      : { borderColor: BRAND_GREEN, color: BRAND_GREEN }),
                  }}
                >
                  {cat.name}
                </Button>
              ))}
            </Box>
          )}
          {paginatedCampaigns.length === 0 ? (
            <Typography sx={{ fontFamily: '"Poppins", sans-serif', textAlign: 'center', color: '#4B4646', py: 6 }}>
              No campaigns available at the moment
            </Typography>
          ) : (
            <>
              <Grid container spacing={4}>
                {paginatedCampaigns.map((campaign, index) => {
                  const columnIndex = index % 3;
                  const theme = CARD_THEMES[columnIndex];
                  const dateStr = formatCampaignDate(campaign.start_date || campaign.end_date || campaign.created_at) || 'Jan 18, 2013';
                  const location = campaign.address || campaign.location || campaign.map_address || campaign.venue || campaign.place || campaign.city || campaign.location_name || '';
                  const label = campaign.label || '';
                  const desc = campaign.description ? campaign.description.replace(/\s+/g, ' ').trim() : 'Children face tough challenges: hunger, malnutrition, limited access to education and healthcare. Your support can change lives.';
                  const descShort = desc.length > 140 ? desc.substring(0, 140) + '...' : desc;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={campaign.id}>
                      <Card
                        elevation={0}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 2,
                          overflow: 'hidden',
                          boxShadow: '0 0 17px 0 rgba(0,0,0,0.06)',
                          '&:hover': { boxShadow: '0 0 24px 0 rgba(0,0,0,0.1)' },
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="220"
                          image={getImageUrl(campaign.image_url) || 'https://via.placeholder.com/400x220?text=Campaign'}
                          alt={campaign.title}
                          sx={{ objectFit: 'cover', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}
                        />
                        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2.5, textAlign: 'left' }}>
                          <Typography
                            component="h3"
                            sx={{
                              fontFamily: '"Nunito", sans-serif',
                              fontWeight: 900,
                              fontSize: 18,
                              color: '#2D2929',
                              mb: 1.5,
                              lineHeight: 1.3,
                            }}
                          >
                            {label || campaign.title}
                          </Typography>
                          {campaign.category?.name && (
                            <Typography
                              sx={{
                                fontFamily: '"Poppins", sans-serif',
                                fontSize: 12,
                                fontWeight: 600,
                                color: theme.accent,
                                mb: 1,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                              }}
                            >
                              {campaign.category.name}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              <AccessTime sx={{ fontSize: 16, color: theme.accent }} />
                              <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 400, fontSize: 13, color: META_GREY }}>
                                {dateStr}
                              </Typography>
                            </Box>
                            <Typography component="span" sx={{ color: META_GREY, fontSize: 13, mx: 0.5 }}>|</Typography>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationOn sx={{ fontSize: 16, color: theme.accent }} />
                              <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 400, fontSize: 13, color: META_GREY }}>
                                {location || '—'}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography
                            sx={{
                              fontFamily: '"Poppins", sans-serif',
                              fontWeight: 400,
                              fontSize: 14,
                              color: META_GREY,
                              lineHeight: 1.6,
                              mb: 2,
                              flexGrow: 1,
                            }}
                          >
                            {descShort}
                          </Typography>
                          <Button
                            variant="contained"
                            disableElevation
                            onClick={() => navigate(getCampaignPath(campaign.slug))}
                            sx={{
                              alignSelf: 'flex-start',
                              borderRadius: 1,
                              backgroundColor: theme.btnBg,
                              color: theme.accent,
                              fontFamily: '"Poppins", sans-serif',
                              fontWeight: 700,
                              fontSize: 13,
                              px: 2,
                              py: 1,
                              textTransform: 'none',
                              '&:hover': { backgroundColor: theme.btnBg, opacity: 0.9, color: theme.accent },
                            }}
                          >
                            Read more &gt;
                          </Button>
                          <Box sx={{ mt: 1.5 }}>
                            <CampaignShareButtons
                              campaignSlug={campaign.slug}
                              title={campaign.title}
                              compact
                              iconSize={30}
                              showLabel={false}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                    renderItem={(item) => (
                      <PaginationItem
                        {...item}
                        sx={{
                          fontFamily: '"Poppins", sans-serif',
                          fontWeight: 600,
                          '&.Mui-selected': {
                            backgroundColor: '#dc2626',
                            color: '#fff',
                            '&:hover': { backgroundColor: '#b91c1c' },
                          },
                        }}
                      />
                    )}
                    sx={{
                      '& .MuiPagination-ul': { gap: 0.5 },
                      '& .MuiPaginationItem-root': { borderRadius: 2 },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Container>
      </Box>

      {/* Join With Us - same as HomePage */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 8 } }}>
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Typography
            sx={{
              fontFamily: '"Caveat", cursive',
              fontWeight: 700,
              fontSize: { xs: 24, md: 27 },
              lineHeight: '100%',
              letterSpacing: 0,
              color: '#313530',
              textAlign: 'center',
              mb: '25px',
            }}
          >
            Join With Us
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: '"Nunito", sans-serif',
              fontWeight: 900,
              fontSize: { xs: 26, sm: 34, md: 40 },
              lineHeight: '1.25em',
              letterSpacing: '0.01em',
              color: '#2D2929',
              textAlign: 'center',
              mb: { xs: '30px', md: '50px' },
            }}
          >
            Join in Roots & Wings
            <br />
            for Better Tomorrow
          </Typography>
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              maxWidth: 1200,
              mx: 'auto',
              borderRadius: { xs: 16, md: '23px' },
              minHeight: { xs: 200, md: 238 },
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #c4a574',
              backgroundImage: `url(${ASSETS.parallax})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(36, 88, 78, 0.63)',
                borderRadius: '23px',
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                justifyContent: 'space-evenly',
                alignItems: 'center',
                flex: 1,
                minHeight: { xs: 200, md: 238 },
                gap: { xs: 3, sm: 0 },
                py: { xs: 4, md: 5 },
                px: { xs: 3, md: 4 },
              }}
            >
              <SiteStatsItems />
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default CampaignsPage;
